// iOS-style detail view for a single audit event. Narrow screens get a
// sticky back-arrow nav bar; the body is a hero block (action tag,
// table name, timestamp) + a grouped "context" card (Settings-style
// label-value rows) + optional Details/Before/After JSON cards.

import type { AuditEvent } from "../data/audit.ts";
import { ActionDot, formatCt, renderBytea } from "./utils.tsx";

export function DetailView({ event, onBack }: { event: AuditEvent; onBack: () => void }) {
  return (
    <div className="min-h-screen md:min-h-full">
      <div className="md:hidden sticky top-0 z-10 bg-slate-50/90 backdrop-blur px-3 py-3 flex items-center">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-[17px] text-[#052460] font-medium -ml-1"
          aria-label="Back to list"
        >
          <svg
            width="10"
            height="18"
            viewBox="0 0 10 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 1L1 9l8 8" />
          </svg>
          <span>Audit</span>
        </button>
      </div>

      <div className="px-5 md:px-8 pt-2 md:pt-10 pb-10 space-y-5">
        <div>
          <div className="flex items-center gap-2">
            <ActionDot action={event.action} />
            <span className="text-[13px] uppercase tracking-wide text-slate-500 font-medium">
              {event.action}
            </span>
          </div>
          <h2 className="mt-1.5 text-[22px] font-semibold font-mono tracking-tight break-all">
            {event.tableSchema}.{event.tableName}
          </h2>
          <p className="mt-1 text-[13px] text-slate-500">{formatCt(event.occurredAt)} CT</p>
        </div>

        <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <ContextRow label="Row">{event.rowId ?? "—"}</ContextRow>
          <ContextRow label="Actor">{event.actorId ?? "—"}</ContextRow>
          <ContextRow label="Device">{event.deviceId ?? "—"}</ContextRow>
          <ContextRow label="Request">{event.requestId ?? "—"}</ContextRow>
          <ContextRow label="Version" last>
            {event.rowVersion}
          </ContextRow>
        </section>

        {event.details != null && <JsonCard title="Details" value={event.details} />}
        {event.before != null && <JsonCard title="Before" value={event.before} />}
        {event.after != null && <JsonCard title="After" value={event.after} />}
      </div>
    </div>
  );
}

function ContextRow({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className={`flex items-start gap-3 px-4 py-3 ${last ? "" : "border-b border-slate-200"}`}>
      <span className="text-[15px] text-slate-500 shrink-0 w-20">{label}</span>
      <span className="text-[15px] text-slate-900 font-mono break-all min-w-0 flex-1 text-right">
        {children}
      </span>
    </div>
  );
}

function JsonCard({ title, value }: { title: string; value: unknown }) {
  return (
    <section className="space-y-2">
      <h3 className="px-4 text-[13px] uppercase tracking-wide text-slate-500 font-medium">
        {title}
      </h3>
      <pre className="rounded-2xl bg-white border border-slate-200 p-4 text-[12px] font-mono overflow-x-auto whitespace-pre-wrap break-all">
        {JSON.stringify(renderBytea(value), null, 2)}
      </pre>
    </section>
  );
}
